import React from 'react';
import ArticleLayout, { FaqList, SectionHeading, linkClassName } from '../components/ArticleLayout';

// Date shown on the page and used in the Article structured data (src/seo.js).
// Procedure based on the official manual "Manifestación de valor portal VUCEM -
// Agregar RFC´s de consulta y/o documentos digitalizados".
export const EDOC_MVE_UPDATED = '2026-10-05';

const REQUISITOS_ARCHIVO = [
  'PDF en escala de grises',
  'Resolución de 300 DPI',
  'Máximo 3 MB por archivo',
  'Sin formularios, objetos OLE incrustados ni código JavaScript',
  'Sin páginas en blanco',
  'Sin contraseña',
];

const OPCIONES = [
  {
    title: 'Digitalizar antes y capturar el e-document',
    description:
      'Digitalizas cada documento en "Digitalizar Documentos de Comercio Exterior", obtienes su e-document y lo capturas en la sección de e-documents de la MVE. Es la vía que usan los sistemas que transmiten por servicio web, porque el servicio web no permite adjuntar archivos.',
  },
  {
    title: 'Adjuntar el PDF dentro de la MVE',
    description:
      'En el portal, al registrar la MVE, adjuntas los PDF en el paso "Anexar requisitos". También puedes agregarlos después con la opción "Agregar RFC´s de consulta y/o documentos digitalizados".',
  },
];

export const EDOC_MVE_PASOS = [
  {
    title: 'Convierte el documento al formato',
    description: 'Deja cada documento en PDF a 300 DPI, en escala de grises, sin páginas en blanco y de máximo 3 MB.',
  },
  {
    title: 'Ingresa con la e.firma del importador',
    description: 'En la ventanilla única, elige "Ventanilla Única" > "Ingreso Solicitante", carga tu certificado (.cer), tu clave privada (.key) y su contraseña, y selecciona tu rol.',
  },
  {
    title: 'Abre el trámite de manifestación de valor',
    description: 'Ve a "Trámites" > "Solicitudes nuevas" > "HACIENDA" y, en el apartado AGA, despliega "Manifestación de valor". Elige "Registro" para una nueva MVE o "Agregar RFC´s de consulta y/o documentos digitalizados" para una ya presentada.',
  },
  {
    title: 'Agrega los documentos',
    description: 'Selecciona el tipo de documento, presiona "Agregar nuevo" por cada archivo, luego "Adjuntar documentos" y "Examinar" para elegir el PDF. Cuando el estatus de carga diga "Correcto", presiona "Cerrar" y "Continuar".',
  },
  {
    title: 'Firma y descarga el acuse',
    description: 'Firma con la e.firma del importador. La ventanilla genera el "Acuse de manifestación de valor" y el "Detalle manifestación de valor" en PDF, con el número de la manifestación (MNVA…).',
  },
];

export const EDOC_MVE_FAQS = [
  {
    question: '¿Qué e-document se declara en el pedimento?',
    answer: 'Solo el de la manifestación de valor (el número MNVA… del acuse). Los documentos soporte generan sus propios e-documents, pero esos no se declaran en el pedimento.',
  },
  {
    question: '¿Puedo agregar documentos a una MVE que ya presenté?',
    answer: 'Sí. En "Manifestación de valor" elige "Agregar RFC´s de consulta y/o documentos digitalizados", busca la MVE por su número o por fechas (máximo 7 días), adjunta los documentos como "Documento complementario" y firma de nuevo con la e.firma del importador.',
  },
  {
    question: '¿Cómo autorizo a mi agente aduanal a consultar la MVE?',
    answer: 'En la sección "RFC´s de consulta" de la MVE, captura el RFC de tu agente o agencia aduanal, elige el tipo de figura y presiona "Continuar". Puedes hacerlo al registrarla o después.',
  },
  {
    question: '¿Por qué la ventanilla rechaza mi PDF?',
    answer: 'Casi siempre por el formato: resolución distinta de 300 DPI, archivo a color, páginas en blanco, más de 3 MB o un PDF con formularios u objetos incrustados. Convierte el archivo antes de subirlo.',
  },
  {
    question: '¿Quién firma cuando agrego documentos a la MVE?',
    answer: 'El importador, con su e.firma, igual que al presentar la MVE.',
  },
];

const EdocumentMvePage = () => (
  <ArticleLayout
    path="/como-digitalizar-edocument-manifestacion-de-valor"
    title="¿Cómo digitalizo un e-document para la Manifestación de Valor Electrónica (MVE)?"
    updated={EDOC_MVE_UPDATED}
    intro={
      <>
        Convierte cada documento soporte a PDF de 300 DPI, en escala de grises, sin páginas en blanco y de máximo 3 MB.
        Luego tienes dos opciones: digitalizarlo antes en la ventanilla única para obtener su e-document y capturarlo en la
        MVE, o adjuntar el PDF directamente al registrar la MVE con la e.firma del importador. Solo el e-document de la
        propia MVE (número MNVA…) se declara en el pedimento.
      </>
    }
    ctaTitle="Digitaliza y presenta tus MVE desde un solo lugar"
    ctaText="EC Traffic digitaliza tus documentos soporte en el formato de la ventanilla única, da seguimiento a cada e-document y prepara tus manifestaciones de valor ligadas a su pedimento y COVE."
  >
    <SectionHeading id="requisitos">Requisitos del archivo</SectionHeading>
    <p className="mt-4 text-gray-600 dark:text-gray-400">
      Según el manual oficial de la ventanilla única, cada documento que adjuntes a la MVE debe cumplir con:
    </p>
    <ul className="mt-4 list-disc space-y-1 pl-6 text-gray-600 dark:text-gray-400">
      {REQUISITOS_ARCHIVO.map((requisito) => (
        <li key={requisito}>{requisito}</li>
      ))}
    </ul>
    <p className="mt-4 text-gray-600 dark:text-gray-400">
      Si tu archivo no cumple, conviértelo con nuestro{' '}
      <a href="/convertidor" className={linkClassName}>convertidor PDF VUCEM gratuito</a>: lo deja a 300 DPI y en escala
      de grises en segundos.
    </p>

    <SectionHeading id="opciones">Dos formas de agregar documentos a la MVE</SectionHeading>
    <div className="mt-6 grid gap-4 sm:grid-cols-2">
      {OPCIONES.map((opcion) => (
        <div
          key={opcion.title}
          className="rounded-lg border border-gray-300 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900"
        >
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">{opcion.title}</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{opcion.description}</p>
        </div>
      ))}
    </div>

    <SectionHeading id="paso-a-paso">Paso a paso en el portal</SectionHeading>
    <ol className="mt-6 space-y-6">
      {EDOC_MVE_PASOS.map((paso, index) => (
        <li key={paso.title} className="flex gap-4">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-700 text-sm font-semibold text-white dark:bg-teal-500 dark:text-gray-900"
            aria-hidden="true"
          >
            {index + 1}
          </span>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{paso.title}</h3>
            <p className="mt-1 text-gray-600 dark:text-gray-400">{paso.description}</p>
          </div>
        </li>
      ))}
    </ol>
    <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
      Los nombres de menús y botones corresponden al manual oficial de la ventanilla única. Desde mayo de 2026 la
      ventanilla opera como VUTCE, por lo que algunas pantallas pueden cambiar.
    </p>

    <SectionHeading id="ec-traffic">Cómo lo hace EC Traffic</SectionHeading>
    <ul className="mt-4 list-disc space-y-2 pl-6 text-gray-600 dark:text-gray-400">
      <li>Digitaliza tus documentos soporte en el formato de la ventanilla única y da seguimiento a cada e-document obtenido.</li>
      <li>Prepara tus manifestaciones de valor ligadas a su pedimento y COVE, con el estado de cada envío.</li>
      <li>La conciliación revisa que estén todos los COVE y e-documents esperados y te avisa si falta alguno.</li>
      <li>Tu empresa, como importadora, firma cada MVE con su e.firma.</li>
    </ul>

    <SectionHeading id="preguntas-frecuentes">Preguntas frecuentes</SectionHeading>
    <FaqList faqs={EDOC_MVE_FAQS} />
  </ArticleLayout>
);

export default EdocumentMvePage;
